import { formatApiError as formatSharedApiError } from "@ishvaram/api-client";
import { env } from "@/env";
import { fetchWithTimeout } from "@/lib/fetch";
import { RetryableHttpError, withResilientRetry } from "./_retry";

export const API_BASE = env.NEXT_PUBLIC_API_URL;
export { fetchWithTimeout };

// Normalize FastAPI error detail into a human-readable string.
// FastAPI returns `{detail: string}` for HTTPException and
// `{detail: [{loc, msg, type, ...}, ...]}` for Pydantic validation errors.
// Throwing `new Error(array)` coerces to "[object Object]" — this helper
// prevents that by flattening validation errors into readable text.
export function formatApiError(body: unknown, status: number): string {
  return formatSharedApiError(body, status);
}

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export async function throwApiError(res: Response, fallback?: string): Promise<never> {
  const body = await res.json().catch(() => ({}));
  throw new ApiError(
    res.status,
    formatApiError(body, res.status) || fallback || `HTTP ${res.status}`
  );
}

/** The governed first-party server-side identity.
 *
 * Every request we make from a Worker SSR render or the prerender box egresses
 * from ONE shared IP — the Cloudflare colo, or the build box. The zone
 * rate-limit rule keys on [cf.colo.id, ip.src], so all of our own server-side
 * traffic collapses onto a single 50-req/10s bucket while real browsers (each
 * with their own IP) sit at 0% 429. Measured 2026-07-29: SSR 28.2% 429,
 * prerender 46.9%, browsers 0%.
 *
 * Cloudflare's own documented fix is `cf.worker.upstream_zone`, which requires
 * a paid Advanced Rate Limiting plan we do not have. The entitled equivalent is
 * to make first-party server traffic *identifiable* and exclude it in the rule
 * expression — the standard "skip rule for known internal callers" pattern.
 *
 * Keep this in step with `first_party_user_agents` in
 * `business/ishvaram/data/ops/cloudflare_platform_policy.yaml`; the guard
 * `tests/test_cloudflare_platform_policy.py` pins them together.
 *
 * Browser requests never carry it — the browser controls its own UA, and a
 * seeker must stay subject to the limit.
 */
export const FIRST_PARTY_USER_AGENT = "IshvaramFirstParty/1.0";

const PRODUCTION_BUILD_PHASE = "phase-production-build";
let productionBuildOriginTail: Promise<void> = Promise.resolve();

function withFirstPartyIdentity(init?: RequestInit): RequestInit | undefined {
  // Browser fetch cannot set User-Agent (it is a forbidden header), and a
  // client request must be rate-limited like any other, so this is server-only.
  if (typeof window !== "undefined") return init;
  const headers = new Headers(init?.headers);
  headers.set("User-Agent", FIRST_PARTY_USER_AGENT);
  return { ...init, headers };
}

async function fetchApiOrigin(
  path: string,
  init?: RequestInit,
  timeoutMs?: number
): Promise<Response> {
  const request = () =>
    fetchWithTimeout(`${API_BASE}${path}`, withFirstPartyIdentity(init), timeoutMs);

  if (process.env.NEXT_PHASE !== PRODUCTION_BUILD_PHASE) return request();

  // A single static page can fan out to many expensive Jyotish reads. Next's
  // page-concurrency setting cannot bound that request fan-out, so serialize at
  // the origin boundary while production pages are being generated. The gate
  // is released in finally so one failed read cannot poison the build queue.
  const precedingRequest = productionBuildOriginTail;
  let releaseRequest!: () => void;
  productionBuildOriginTail = new Promise<void>((resolve) => {
    releaseRequest = resolve;
  });

  await precedingRequest;
  try {
    return await request();
  } finally {
    releaseRequest();
  }
}

export async function apiRequest<T>(
  path: string,
  init?: RequestInit,
  timeoutMs?: number
): Promise<T> {
  const res = await fetchApiOrigin(path, init, timeoutMs);
  if (!res.ok) await throwApiError(res);
  return res.json() as Promise<T>;
}

// Route deterministic, cacheable GET reads (panchang/choghadiya/muhurat —
// same (city,date) always yields the same result) through the same-origin
// Cloudflare edge proxy at /_jyotish/* so popular combinations are served
// from CF's cache instead of recomputing on Cloud Run every hit.
//
// SEO-safe by construction:
//   - client-only: SSR and unit tests use the direct origin API (a
//     worker->worker self-call would add latency, not save it);
//   - same-origin: no CORS, no new DNS/subdomain, no production routing change;
//   - graceful fallback: ANY proxy error falls back to the direct API, so a
//     page render can never break because the edge cache hiccuped.
export async function edgeRead<T>(
  endpoint: string,
  search: string,
  timeoutMs?: number
): Promise<T> {
  const directPath = `/${endpoint}${search ? `?${search}` : ""}`;
  if (typeof window === "undefined") {
    return withResilientRetry(
      async () => {
        try {
          return await apiRequest<T>(directPath, undefined, timeoutMs);
        } catch (error) {
          if (error instanceof ApiError && error.status >= 500) {
            throw new RetryableHttpError(error.status, error.message);
          }
          throw error;
        }
      },
      {
        idempotencyKey: "server-read",
        maxAttempts: 3,
        baseDelayMs: 100,
        maxDelayMs: 1_000,
      }
    );
  }
  try {
    const res = await fetchWithTimeout(
      `/_jyotish/${endpoint}${search ? `?${search}` : ""}`,
      undefined,
      timeoutMs
    );
    if (!res.ok) throw new ApiError(res.status, `edge proxy HTTP ${res.status}`);
    return (await res.json()) as T;
  } catch {
    return apiRequest<T>(directPath, undefined, timeoutMs);
  }
}

export async function sameOriginRequest<T>(
  path: string,
  init?: RequestInit,
  timeoutMs?: number
): Promise<T> {
  const res = await fetchWithTimeout(path, init, timeoutMs);
  if (!res.ok) await throwApiError(res);
  return res.json() as Promise<T>;
}

export async function authJsonHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  try {
    const { getSupabase } = await import("@/lib/supabase");
    const {
      data: { session },
    } = await getSupabase().auth.getSession();
    if (session?.access_token) {
      headers.Authorization = `Bearer ${session.access_token}`;
    }
  } catch {
    // Anonymous and test environments should still use the public API paths.
  }
  return headers;
}

export async function apiBlobRequest(
  path: string,
  init?: RequestInit,
  timeoutMs?: number
): Promise<Blob> {
  const res = await fetchApiOrigin(path, init, timeoutMs);
  if (!res.ok) await throwApiError(res);
  return res.blob();
}
